import { useState, useEffect } from "react";
import { Modal } from "./Modal";
import { Button } from "../ui/button";
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { validateName } from "@/lib/utils";
import { useUpsertPortfolio } from "@/hooks/queryHooks";
import type { PortfolioPayload } from "global-types";

export const EditPortfolioModal = () => {
  const { closeModal, getModalState } = useModalNavigation();
  const modalState = getModalState();
  const accountId = modalState?.accountId;
  const portfolioId = modalState?.portfolioId;
  const updatePortfolioMutation = useUpsertPortfolio();
  const { error: mutationError, isPending } = updatePortfolioMutation;

  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
  });

  useEffect(() => {
    if (portfolioId) {
      setFormData({
        name: "",
      });
    }
  }, [portfolioId]);

  useEffect(() => {
    if (mutationError) {
      setError(mutationError.message);
    }
  }, [mutationError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const resetForm = () => {
    setFormData({
      name: "",
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nameValidationError = validateName(formData.name);
    if (nameValidationError) {
      setError(nameValidationError);
      return;
    }

    try {
      const portfolioData: PortfolioPayload = {
        accountId: accountId || "",
        name: formData.name.trim(),
        id: portfolioId,
      };

      await updatePortfolioMutation.mutateAsync(portfolioData);

      closeModal();
      resetForm();
    } catch (err) {
      console.error("Failed to update portfolio:", err);
    }
  };

  const handleCancel = () => {
    closeModal();
    resetForm();
  };

  return (
    <Modal
      title="Edit Portfolio"
      description="Update the portfolio information below."
      onClose={handleCancel}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Portfolio Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={isPending}
            maxLength={255}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            placeholder="Enter portfolio name"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.name.length}/255 characters
          </p>
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            className="bg-lime text-foreground cursor-pointer hover:text-background"
            type="submit"
            disabled={isPending}
          >
            {isPending ? "Updating..." : "Update Portfolio"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
